import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { 
  LayoutDashboard,
  Video,
  BarChart3,
  Plus,
  Play,
  Users,
  Star,
  Clock,
  Edit,
  Trash2,
  ChevronLeft,
  Loader2,
  Save,
  X,
  Mail,
  Lock,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Instructor Login Component
const InstructorLogin = () => {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already logged in as instructor/admin, redirect to portal
    if (isAuthenticated && (user?.role === 'instructor' || user?.role === 'admin')) {
      navigate('/instructor');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);
      if (userData.role === 'instructor' || userData.role === 'admin') {
        navigate('/instructor');
      } else {
        setError('This account does not have instructor access. Please use a valid instructor account.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <PageTitle title="Instructor Login" />
      
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/" size="default" />
          <Link to="/login">
            <Button variant="ghost" className="text-zinc-400 hover:text-white">
              Member Login
            </Button>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-400/20 rounded-full mb-4">
              <Video className="w-8 h-8 text-teal-400" />
            </div>
            <h1 className="text-3xl font-bold mb-2">INSTRUCTOR PORTAL</h1>
            <p className="text-zinc-500">Sign in to manage your classes and view analytics</p>
          </div>

          <div className="bg-[#121212] border border-zinc-800 rounded-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-400">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#090D0F] border-zinc-800 h-12 text-white"
                    placeholder="instructor@lagreeconnect.com"
                    required
                    data-testid="instructor-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-400">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-[#090D0F] border-zinc-800 h-12 text-white"
                    placeholder="Your password"
                    required
                    data-testid="instructor-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-400 text-black font-bold h-12 hover:bg-teal-300"
                data-testid="instructor-login-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In to Portal
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
              <p className="text-zinc-500 text-sm">
                Not an instructor?{' '}
                <Link to="/login" className="text-teal-400 hover:underline">
                  Member Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InstructorPortal = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [classes, setClasses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);

  const isInstructorOrAdmin = isAuthenticated && (user?.role === 'instructor' || user?.role === 'admin');

  useEffect(() => {
    if (isInstructorOrAdmin) {
      fetchData();
    }
  }, [activeTab, isInstructorOrAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await api.get('/instructor-portal/dashboard');
        setDashboard(res.data);
      } else if (activeTab === 'classes') {
        const res = await api.get('/instructor-portal/classes');
        setClasses(res.data);
      } else if (activeTab === 'analytics') {
        const res = await api.get('/instructor-portal/analytics');
        setAnalytics(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Show login page if not authenticated or not instructor/admin
  if (!isInstructorOrAdmin) {
    return <InstructorLogin />;
  }

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    
    try {
      await api.delete(`/instructor-portal/classes/${classId}`);
      toast.success('Class deleted');
      setClasses(classes.filter(c => c.id !== classId));
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  return (
    <div className="min-h-screen bg-[#090D0F] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A0D0F] border-r border-zinc-800 flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Logo linkTo="/instructor" size="default" />
          <span className="text-xs text-teal-400 font-medium mt-2 block">INSTRUCTOR PORTAL</span>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'dashboard' ? 'bg-teal-400/10 text-teal-400' : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('classes')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'classes' ? 'bg-teal-400/10 text-teal-400' : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <Video className="w-5 h-5" />
            My Classes
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'analytics' ? 'bg-teal-400/10 text-teal-400' : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            Analytics
          </button>
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <Link to="/dashboard">
            <Button variant="ghost" className="w-full justify-start text-zinc-400">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to App
            </Button>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-10 bg-[#090D0F]/80 backdrop-blur border-b border-zinc-800 px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">
              {activeTab === 'dashboard' && 'Dashboard'}
              {activeTab === 'classes' && 'My Classes'}
              {activeTab === 'analytics' && 'Analytics'}
            </h1>
            {activeTab === 'classes' && (
              <Button 
                onClick={() => { setEditingClass(null); setShowCreateModal(true); }}
                className="bg-teal-400 text-black font-bold hover:bg-teal-300"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Class
              </Button>
            )}
          </div>
        </header>

        <div className="p-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
            </div>
          ) : (
            <>
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && dashboard && (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard icon={Video} value={dashboard.stats.total_classes} label="Total Classes" />
                    <StatCard icon={Users} value={dashboard.stats.total_views.toLocaleString()} label="Total Views" />
                    <StatCard icon={Star} value={dashboard.stats.average_rating} label="Avg Rating" />
                    <StatCard icon={Play} value={dashboard.stats.live_classes_scheduled} label="Live Scheduled" />
                  </div>

                  <div className="bg-[#121416] border border-zinc-800 rounded-sm">
                    <div className="p-6 border-b border-zinc-800">
                      <h2 className="text-lg font-bold">Recent Classes</h2>
                    </div>
                    <div className="divide-y divide-zinc-800">
                      {dashboard.recent_classes?.map((cls) => (
                        <div key={cls.id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <img src={cls.thumbnail_url} alt="" className="w-16 h-12 object-cover rounded-sm" />
                            <div>
                              <p className="font-medium">{cls.title}</p>
                              <p className="text-sm text-zinc-500">{cls.duration_minutes} min • {cls.difficulty}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-zinc-400">
                            <span>{cls.participant_count} views</span>
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400" />
                              {cls.rating || 'N/A'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Classes Tab */}
              {activeTab === 'classes' && (
                <div className="bg-[#121416] border border-zinc-800 rounded-sm">
                  <div className="divide-y divide-zinc-800">
                    {classes.length === 0 ? (
                      <div className="p-12 text-center">
                        <Video className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">No Classes Yet</h3>
                        <p className="text-zinc-500 mb-4">Create your first class to get started</p>
                        <Button onClick={() => setShowCreateModal(true)} className="bg-teal-400 text-black">
                          <Plus className="w-4 h-4 mr-2" /> Create Class
                        </Button>
                      </div>
                    ) : (
                      classes.map((cls) => (
                        <div key={cls.id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <img src={cls.thumbnail_url} alt="" className="w-20 h-14 object-cover rounded-sm" />
                            <div>
                              <p className="font-medium">{cls.title}</p>
                              <p className="text-sm text-zinc-500">
                                {cls.duration_minutes} min • {cls.difficulty} • {cls.category}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingClass(cls); setShowCreateModal(true); }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteClass(cls.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <>
                  {analytics ? (
                    <div className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <StatCard icon={Video} value={analytics.total_classes} label="Total Classes" />
                        <StatCard icon={Users} value={analytics.total_views.toLocaleString()} label="Total Views" />
                        <StatCard icon={Star} value={analytics.average_rating} label="Average Rating" />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                          <h3 className="font-bold mb-4">Classes by Category</h3>
                          {Object.entries(analytics.classes_by_category || {}).length > 0 ? (
                            Object.entries(analytics.classes_by_category || {}).map(([cat, count]) => (
                              <div key={cat} className="flex justify-between py-2 border-b border-zinc-800 last:border-0">
                                <span className="text-zinc-400 capitalize">{cat.replace('_', ' ')}</span>
                                <span className="font-bold">{count}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-zinc-500 text-sm">No classes created yet</p>
                          )}
                        </div>
                        <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                          <h3 className="font-bold mb-4">Classes by Difficulty</h3>
                          {Object.entries(analytics.classes_by_difficulty || {}).length > 0 ? (
                            Object.entries(analytics.classes_by_difficulty || {}).map(([diff, count]) => (
                              <div key={diff} className="flex justify-between py-2 border-b border-zinc-800 last:border-0">
                                <span className="text-zinc-400 capitalize">{diff}</span>
                                <span className="font-bold">{count}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-zinc-500 text-sm">No classes created yet</p>
                          )}
                        </div>
                      </div>

                      <div className="bg-[#121416] border border-zinc-800 rounded-sm">
                        <div className="p-6 border-b border-zinc-800">
                          <h3 className="font-bold">Top Rated Classes</h3>
                        </div>
                        <div className="divide-y divide-zinc-800">
                          {analytics.top_rated_classes?.length > 0 ? (
                            analytics.top_rated_classes.map((cls) => (
                              <div key={cls.id} className="p-4 flex items-center justify-between">
                                <span>{cls.title}</span>
                                <span className="flex items-center gap-1 text-yellow-400">
                                  <Star className="w-4 h-4" /> {cls.rating}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="p-6 text-center text-zinc-500">
                              <p>No rated classes yet</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <BarChart3 className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
                      <h3 className="text-xl font-bold mb-2">No Analytics Data Yet</h3>
                      <p className="text-zinc-500 max-w-md mx-auto">
                        Once you create classes and students start taking them, you'll see detailed analytics here including views, ratings, and performance metrics.
                      </p>
                      <Button 
                        onClick={() => setActiveTab('classes')} 
                        className="mt-6 bg-teal-400 text-black hover:bg-teal-300"
                      >
                        Create Your First Class
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Create/Edit Class Modal */}
      {showCreateModal && (
        <ClassFormModal
          editingClass={editingClass}
          onClose={() => { setShowCreateModal(false); setEditingClass(null); }}
          onSave={() => { setShowCreateModal(false); setEditingClass(null); fetchData(); }}
          api={api}
        />
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
    <div className="flex items-center justify-between mb-4">
      <span className="text-zinc-500 text-sm">{label}</span>
      <Icon className="w-5 h-5 text-teal-400" />
    </div>
    <p className="text-3xl font-bold">{value}</p>
  </div>
);

const ClassFormModal = ({ editingClass, onClose, onSave, api }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: editingClass?.title || '',
    description: editingClass?.description || '',
    duration_minutes: editingClass?.duration_minutes || 45,
    difficulty: editingClass?.difficulty || 'intermediate',
    category: editingClass?.category || 'full_body',
    focus_areas: editingClass?.focus_areas?.join(', ') || '',
    spring_load: editingClass?.spring_load || '',
    exercises: editingClass?.exercises?.join(', ') || '',
    class_type: editingClass?.class_type || 'on_demand'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        ...form,
        focus_areas: form.focus_areas.split(',').map(s => s.trim()).filter(Boolean),
        exercises: form.exercises.split(',').map(s => s.trim()).filter(Boolean),
        equipment_needed: ['Megaformer']
      };
      
      if (editingClass) {
        await api.put(`/instructor-portal/classes/${editingClass.id}`, data);
        toast.success('Class updated');
      } else {
        await api.post('/instructor-portal/classes', data);
        toast.success('Class created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121416] border border-zinc-800 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold">{editingClass ? 'Edit Class' : 'Create New Class'}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              className="mt-1 bg-[#090D0F] border-zinc-700"
              required
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="mt-1 w-full bg-[#090D0F] border border-zinc-700 rounded-sm p-3 text-white resize-none h-24"
              required
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Duration (min)</Label>
              <Input
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value)})}
                className="mt-1 bg-[#090D0F] border-zinc-700"
              />
            </div>
            <div>
              <Label>Difficulty</Label>
              <select
                value={form.difficulty}
                onChange={(e) => setForm({...form, difficulty: e.target.value})}
                className="mt-1 w-full bg-[#090D0F] border border-zinc-700 rounded-sm p-2 text-white"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <Label>Category</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})}
                className="mt-1 w-full bg-[#090D0F] border border-zinc-700 rounded-sm p-2 text-white"
              >
                <option value="full_body">Full Body</option>
                <option value="core">Core</option>
                <option value="lower_body">Lower Body</option>
                <option value="upper_body">Upper Body</option>
                <option value="express">Express</option>
                <option value="fundamentals">Fundamentals</option>
              </select>
            </div>
          </div>
          
          <div>
            <Label>Focus Areas (comma-separated)</Label>
            <Input
              value={form.focus_areas}
              onChange={(e) => setForm({...form, focus_areas: e.target.value})}
              placeholder="e.g., Glutes, Core, Arms"
              className="mt-1 bg-[#090D0F] border-zinc-700"
            />
          </div>
          
          <div>
            <Label>Spring Load</Label>
            <Input
              value={form.spring_load}
              onChange={(e) => setForm({...form, spring_load: e.target.value})}
              placeholder="e.g., 2-3 yellow springs"
              className="mt-1 bg-[#090D0F] border-zinc-700"
            />
          </div>
          
          <div>
            <Label>Exercises (comma-separated)</Label>
            <Input
              value={form.exercises}
              onChange={(e) => setForm({...form, exercises: e.target.value})}
              placeholder="e.g., Elevator Lunge, Catfish, Scrambled Eggs"
              className="mt-1 bg-[#090D0F] border-zinc-700"
            />
          </div>
          
          <div className="flex gap-4 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-zinc-700">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-teal-400 text-black font-bold hover:bg-teal-300">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Class</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstructorPortal;
